import LegalShell from "./LegalShell";

export default function DPDP() {
  return (
    <LegalShell title="DPDP Act Compliance Statement" updated="10 February 2026" path="/legal/dpdp" description="Viyora DPDP Act 2023 compliance statement — data fiduciary/processor roles, breach notification, and patient rights.">
      <p>This document explains how Viyora Tech (“Viyora”) complies with the <strong>Digital Personal Data Protection Act, 2023</strong> (“DPDP Act”), India’s primary personal-data-protection legislation, and its associated rules. It is intended for our clinic customers, patients whose records are stored on the platform, auditors, and regulators.</p>

      <h2>1. Roles under the DPDP Act</h2>
      <ul>
        <li><strong>Clinic (customer)</strong> — Data Fiduciary. Determines the purpose and means of processing patient records.</li>
        <li><strong>Viyora</strong> — Data Processor. Processes personal data on behalf of, and per the instructions of, the clinic.</li>
        <li><strong>Patient / clinic staff</strong> — Data Principal. The natural person whose personal data is being processed.</li>
      </ul>

      <h2>2. Principles we honour</h2>
      <ul>
        <li><strong>Lawful, fair, transparent processing</strong> — Every purpose is disclosed at collection.</li>
        <li><strong>Purpose limitation</strong> — Data is used only for the specified clinical / operational purpose.</li>
        <li><strong>Data minimisation</strong> — We do not require fields that aren’t clinically or legally necessary.</li>
        <li><strong>Accuracy</strong> — Clinics can edit patient records at any time; audit trail preserved.</li>
        <li><strong>Storage limitation</strong> — Retention aligned with clinical, tax, and legal necessity (see Privacy Policy §5).</li>
        <li><strong>Accountability</strong> — All processing is logged; sub-processors are contractually bound.</li>
      </ul>

      <h2>3. Consent framework</h2>
      <p>Consent captured on Viyora is:</p>
      <ul>
        <li><strong>Free</strong> — obtained without coercion or bundled with unrelated services.</li>
        <li><strong>Specific</strong> — separated per purpose (treatment consent, marketing communication, data storage).</li>
        <li><strong>Informed</strong> — the patient sees a plain-language description of the treatment, risks, and warranty before signing.</li>
        <li><strong>Unambiguous</strong> — recorded as a digitally-signed document with timestamp and IP address.</li>
        <li><strong>Withdrawable</strong> — the patient can revoke consent through the clinic; withdrawal takes effect prospectively.</li>
      </ul>

      <h2>4. Security safeguards (Section 8(5), DPDP Act)</h2>
      <ul>
        <li>Encryption in transit (TLS 1.2+) and at rest (AES-256).</li>
        <li>Multi-tenant isolation with <code>clinic_id</code>-scoped queries and role-based access.</li>
        <li>Bcrypt password hashing; short-lived JWT tokens; JWKS-verified social login via Supabase.</li>
        <li>Signed webhooks (HMAC-SHA256) for all payment events.</li>
        <li>Access to production systems restricted to a small named team; access reviewed every 90 days.</li>
        <li>Immutable audit logs for consent forms, warranty issuance, invoices, and role changes.</li>
      </ul>

      <h2>5. Data-breach notification</h2>
      <p>In the event of a personal-data breach affecting the confidentiality, integrity, or availability of records on the platform, Viyora will:</p>
      <ul>
        <li>Notify the Data Protection Board of India within 72 hours of confirmation, as required.</li>
        <li>Notify affected clinics (Data Fiduciaries) without undue delay so they can, in turn, notify their patients.</li>
        <li>Publish a post-incident report within 30 days including root cause and remediation.</li>
      </ul>

      <h2>6. Data Principal rights & how to exercise them</h2>
      <ul>
        <li><strong>Right to access</strong> — Log in and export your data, or email <a href="mailto:admin@viyora.co.in">admin@viyora.co.in</a>.</li>
        <li><strong>Right to correction & erasure</strong> — Patients contact their clinic; the clinic contacts us if the change requires backend action.</li>
        <li><strong>Right to grievance redressal</strong> — Any Data Principal may raise a complaint with our DPO (below). Unresolved grievances may be escalated to the Data Protection Board of India.</li>
        <li><strong>Right to nominate</strong> — Users can nominate an heir to receive their data (contact DPO to record).</li>
      </ul>

      <h2>7. Cross-border transfer</h2>
      <p>Primary data storage is located in India (Mumbai region). Sub-processors that operate outside India (e.g., Anthropic for AI inference) are engaged only under written data-processing agreements with standard contractual clauses, and only for the narrow purposes described in our Privacy Policy.</p>

      <h2>8. Children’s data (Section 9)</h2>
      <p>Personal data of minors (below 18) is processed only after verifiable parental / guardian consent captured through the digital consent flow. Behavioural tracking and targeted advertising of children are prohibited on the platform.</p>

      <h2>9. Grievance officer</h2>
      <p>Data Protection Officer, Viyora Tech<br />Email: <a href="mailto:admin@viyora.co.in">admin@viyora.co.in</a><br />Statutory response window: 15 working days from receipt.</p>

      <h2>10. Amendments</h2>
      <p>This statement is reviewed at least once every 12 months and whenever the DPDP Act, associated rules, or our processing activities change materially.</p>
    </LegalShell>
  );
}
